import React, { useEffect, useRef, useState } from 'react';
import Betslip from '../../common/Betslip';
import GoUpBtn from '../../common/GoUpBtn';
import GoUpParag from '../../common/GoUpParag';
import Loader from '../../common/Loader';
import { useFetchCalendarData } from '../../data/fetchCalendarData';
import { getMatchStreamingProps } from '../../data/streaming/streamingUtils';
import { classIf, getConfiguration } from '../../data/utils';
import { getLabel } from '../../InitData';
import CalendarDateSelector from './CalendarDateSelector';
import CalendarMatch from './CalendarMatch';
import CalendarSportSelector from './CalendarSportSelector';
import SeoMainContainer from '../../data/seo/SeoMainContainer';
import SeoMetaTags from '../../data/seo/SeoMetaTags';
import { useFetchSeoSettings } from '../../data/fetchSeoSettings';
import { useHandleSeoSettings } from '../../data/seo/seoUtils';
import { SeoContext } from '../../data/seo/SeoContextProvider';
import MainContext from '../../contexts/MainContext';

function sortEvents(data, date, type) {
    const asc = type === 'asc';
    const sports = data.dateById[date].sports;
    sports.forEach(s => {
        s.events.sort((a, b) => {
            if (asc) {
                return a.timestampDB.localeCompare(b.timestampDB);
            } else {
                return b.timestampDB.localeCompare(a.timestampDB);
            }
        });
    });
    sports.sort((a, b) => {
        if (asc) {
            return a.events[0].timestampDB.localeCompare(b.events[0].timestampDB);
        } else {
            return b.events[0].timestampDB.localeCompare(a.events[0].timestampDB);
        }
    });
}

function Calendar(props) {
    const conf = getConfiguration();
    const mainContext = React.useContext(MainContext);
    const cfgSeo = conf.seo;
    const seoContext = React.useContext(SeoContext);
    const [seoSettingsState] = useFetchSeoSettings(seoContext.pathname,false,cfgSeo);
    const calendarOnlyView = mainContext.hideBetslip && mainContext.hideMenu;
    const refScrollableContainer = useRef();

    useHandleSeoSettings(seoSettingsState);

    // Abilita scrolling
    useEffect(function() {
		window.setHeight();
    });

    const [state, setState] = useState({
        showLoader: true,
        data: null,
        dateFetch: '',
        date: null,
        sport: null,
        sortType: 'asc',
        sortMenuToggled: false,
        update(updt) {
            setState(prev => {
                return {...prev, ...updt};
            });
        },
        setShowLoader(showLoader) {
            this.update({showLoader});
        },
        setData(data) {
            let date = null;
            if (this.dateFetch === '') {
                date = data.dates[0];
            } else {
                date = data.dates.find(d => { return d.DBDate === this.dateFetch });
                if (this.sortType !== 'asc') {
                    sortEvents(data, date.DBDate, this.sortType);
                }
            }
            this.update({showLoader: false, data, date: date.DBDate, sport: date.sports[0].id});
        },
        setDate(dateFetch) {
            if (dateFetch !== this.date) {
                this.update({dateFetch});
            }
        },
        setSport(sport) {
            this.update({sport});
        },
        setSortMenuToggled(sortMenuToggled) {
            this.update({sortMenuToggled});
        },
        changeSortType(type) {
            if (type !== this.sortType) {
                if (type !== 'league') {
                    sortEvents(this.data, this.date, type);
                    this.update({data: this.data, sortType: type});
                }
            }
        }
    });

    function changeDate(date) {
        state.setDate(date, null);
    }

    function changeSport(sportId) {
        state.setSport(sportId);
    }

    const [request] = useFetchCalendarData(state.dateFetch);

    useEffect(() => {
        let timeout = null;
        if (request.isLoading) {
            timeout = setTimeout(function() {
                state.setShowLoader(true);
            }, conf.lspt);
        } else if (!request.isError) {
            clearTimeout(false);
            state.setData(request.data);
        } else {
            state.setShowLoader(true);
        }
        
        return () => {
            clearInterval(timeout);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request]);

    if (state.showLoader) {
        return <Loader error={request.isError} />;
    }

    const datesList = state.data.dates.map(function(date) {
        return (
            <CalendarDateSelector key={date.DBDate} date={date} selected={date.DBDate === state.date} changeDate={changeDate} />
        );
    });

    const sportsList = state.data.dateById[state.date].sports.map(function(sport) {
        return (
            <CalendarSportSelector key={sport.id} {...sport} selected={sport.id === state.sport} changeSport={changeSport} />
        );
    });

    const currentSportData = state.data.dateById[state.date].sportById[state.sport];
    const matchesList = currentSportData.events.map(function(match) {
        const streamingMap = getMatchStreamingProps(match);
        return (
            <CalendarMatch key={match.id} sport={currentSportData.desc} competition={match.competition} match={match.desc} time={match.time} streaming={streamingMap && streamingMap[match.id]}  />
        );
    });

    return (
        <>
            <SeoMetaTags />
            <div className={`container-fluid pagina-sport bg-grigio-chiaro padding-su-1x ${calendarOnlyView?'calendarOnlyView':''}`}>
                <div className="row no-gutters sport-griglia">
                    <div className="griglia-centrale-sport col stringimento-sidebar calendario-scroll" id="div-centrale">
                        <div className="container-calendario bordo-tondo h-100 p-2">
                            <div ref={refScrollableContainer}  className="col-md-12 h-100 p-2 bordo-tondo sport-blocco go-up-arrow-on-overflow">
                                <GoUpBtn />
                                <div className="blocco-filtro-calendario nero">
                                    <div className="filtro-giorni-cal primo-filtro d-flex justify-content-start align-items-center p-3">
                                        <span className="grassetto colore-1"><i className="fas fa-calendar-alt"></i></span>
                                        {datesList}
                                    </div>
                                    <div className="filtro-giorni-cal secondo-filtro d-flex justify-content-start align-items-center p-3">
                                        {sportsList}
                                        <div className="sort-button position-relative" onClick={() => {state.setSortMenuToggled(!state.sortMenuToggled)}}>
                                            <i className="fa fa-sort-alpha-down"></i>
                                            <span>{getLabel('sort_menu.title')}</span>
                                            {state.sortMenuToggled &&
                                                <div className="nav-slider-menu">
                                                    <div className="nav-slider-menu-close-button push-right pointer" onClick={() => {state.setSortMenuToggled(!state.sortMenuToggled)}}>
                                                        <i className="fa fa-times"></i>
                                                    </div>
                                                    <div className={"nav-slider-menu-row pointer" + classIf(state.sortType === 'desc', 'selected')} onClick={() => {state.changeSortType('desc')}}>
                                                        <i className="fa fa-sort-down"></i>
                                                        <span>{getLabel('sort_menu.sort_by_chronological_order')}</span>
                                                    </div>
                                                    <div className={"nav-slider-menu-row pointer" + classIf(state.sortType === 'asc', 'selected')} onClick={() => {state.changeSortType('asc')}}>
                                                        <i className="fa fa-sort-up"></i>
                                                        <span>{getLabel('sort_menu.sort_by_reverse_chronological_order')}</span>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div className="container-tabella-calendario">
                                    <div className="scroller-calendario">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th>{getLabel('live_page.calendar.sport')}</th>
                                                    <th>{getLabel('live_page.calendar.tournament')}</th>
                                                    <th>{getLabel('live_page.calendar.event')}</th>
                                                    <th>{getLabel('live_page.calendar.time')}</th>
                                                </tr>
                                                <tr className="spaziatore-tab">
                                                    <th colSpan="4"></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {matchesList}
                                            </tbody>
                                        </table>
                                        <GoUpParag refScrollableContainer={refScrollableContainer} />
                                    </div>
                                </div>
                                <SeoMainContainer />
                            </div>
                        </div>
                    </div>
                    {!mainContext.hideBetslip && 
                     <Betslip />
                    }
                </div>
            </div>
        </>
    );
}

export default Calendar;